import { IApplicableMembershipBenefits } from "@faire/web-api/indigofair/data/IApplicableMembershipBenefits";
import { IInsiderLabel } from "@faire/web-api/indigofair/data/IInsiderLabel";
import { useViewport } from "@faire/web/ui/hooks/useViewport";
import React from "react";

import { InsiderBadge } from "@faire/retailer/components/InsiderBadge/InsiderBadge";
import { useInsiderState } from "@faire/retailer/hooks/insider/useInsiderState";
import { useRetailerStore } from "@faire/retailer/stores/domain/RetailerStore";

interface IInsiderTileBadgeRendererProps {
  className?: string;
  insiderBenefits?: IApplicableMembershipBenefits;
  insiderLabel?: IInsiderLabel;
}

export const InsiderTileBadgeRenderer: React.FC<
  IInsiderTileBadgeRendererProps
> = (props) => {
  const { isInsider: isInsiderEligibleOrActive } = useInsiderState();

  const hasInsiderBenefitsForBrand = !!props.insiderBenefits?.benefits?.length;
  const shouldShowBadging = isInsiderEligibleOrActive;

  if (props.insiderLabel === undefined) {
    return null;
  }

  if (shouldShowBadging && hasInsiderBenefitsForBrand) {
    return <InsiderTileBadgeRendererContent {...props} />;
  }

  return null;
};

const InsiderTileBadgeRendererContent: React.FC<
  IInsiderTileBadgeRendererProps
> = ({ className, insiderBenefits, insiderLabel }) => {
  const { isAmerican } = useRetailerStore(["isAmerican"]);
  const { isInsiderActive } = useInsiderState();

  const shouldAdvertiseFreeImportDutiesBenefit = !isAmerican;

  let hasFreeDuties: boolean = false;

  if (isInsiderActive || shouldAdvertiseFreeImportDutiesBenefit) {
    hasFreeDuties = !!insiderBenefits?.benefits.includes(
      IApplicableMembershipBenefits.ApplicableMembershipBenefitType
        .INSIDER_FREE_DUTIES
    );
  }

  const hasFreeShipping = !!insiderBenefits?.benefits.includes(
    IApplicableMembershipBenefits.ApplicableMembershipBenefitType
      .INSIDER_FREE_SHIPPING
  );

  return (
    <TileBadge
      className={className}
      hasFreeDuties={hasFreeDuties}
      hasFreeShipping={hasFreeShipping}
      insiderBadgeText={insiderLabel?.text}
    />
  );
};

interface ITileBadgeProps {
  className?: string;
  hasFreeDuties?: boolean;
  hasFreeShipping?: boolean;
  insiderBadgeText?: string;
}

const TileBadge: React.FC<ITileBadgeProps> = ({
  className,
  hasFreeDuties,
  hasFreeShipping,
  insiderBadgeText,
}) => {
  const showShortenedText = useViewport().isTabletAndBelow;

  return (
    <InsiderBadge
      className={className}
      showShortText={showShortenedText}
      showFreeDuties={hasFreeDuties}
      showFreeShipping={hasFreeShipping}
      insiderBadgeText={insiderBadgeText}
    />
  );
};
